import {
	useEffect,
	useRef,
	useState,
	type ImgHTMLAttributes,
} from "react";

type DeferredImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
	fallbackSrc?: string;
	rootMargin?: string;
	src: string;
};

const DeferredImage = ({
	fallbackSrc,
	rootMargin = "0px",
	src,
	...props
}: DeferredImageProps) => {
	const imageRef = useRef<HTMLImageElement>(null);
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		const image = imageRef.current;
		if (!image) return;

		if (!("IntersectionObserver" in window)) {
			setShouldLoad(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry?.isIntersecting) return;
				setShouldLoad(true);
				observer.disconnect();
			},
			{ rootMargin },
		);

		observer.observe(image);
		return () => observer.disconnect();
	}, [rootMargin]);

	return (
		<img
			{...props}
			ref={imageRef}
			src={shouldLoad ? src : undefined}
			onError={fallbackSrc ? (event) => {
				if (event.currentTarget.src.endsWith(fallbackSrc)) return;
				event.currentTarget.src = fallbackSrc;
			} : undefined}
		/>
	);
};

export default DeferredImage;
